import React from 'react';
import Markdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkSupersub from 'remark-supersub';

import { Heading, Text } from '@eluve/components';

interface ChatMarkdownProps {
  children: string;
}

const markdownComponents: Components = {
  ul: ({ children }) => (
    <ul className="my-2 ml-4 list-outside list-disc space-y-1">{children}</ul>
  ),
  ol: ({ children, start }) => (
    <ol start={start} className="my-2 ml-4 list-outside list-decimal space-y-1">
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li className="my-0.5 pl-1">
      <Text inline className="leading-relaxed">
        {children}
      </Text>
    </li>
  ),
  h1: ({ children }) => (
    <Heading size="xl" weight="bold" className="mt-4 mb-2">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Text size="md" weight="bold" className="mt-3 mb-2">
      {children}
    </Text>
  ),
  h3: ({ children }) => (
    <Text weight="bold" className="mt-3 mb-2">
      {children}
    </Text>
  ),
  h4: ({ children }) => (
    <Text size="xs" weight="bold" className="mt-3 mb-2">
      {children}
    </Text>
  ),
  strong: ({ children }) => (
    <Text inline weight="bold">
      {children}
    </Text>
  ),
  em: ({ children }) => (
    <Text inline color="secondary">
      {children}
    </Text>
  ),
  p: ({ children }) => (
    <Text className="my-3 leading-relaxed whitespace-pre-line">{children}</Text>
  ),
  sup: ({ children, ...props }) => {
    // Extract citation number (e.g., "[1]" -> "1")
    const childrenStr = String(children);
    const citationMatch = childrenStr.match(/^\[(\d+)\]$/);
    const citationNumber = citationMatch ? citationMatch[1] : null;

    return (
      <sup
        {...props}
        className="cursor-pointer text-text-lightblue hover:text-text-lightblue-strong"
        data-citation={citationNumber}
      >
        {children}
      </sup>
    );
  },
};

export { markdownComponents };

export const remarkPlugins = [remarkGfm, remarkSupersub as any];

export const ChatMarkdown: React.FC<ChatMarkdownProps> = ({ children }) => {
  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkSupersub as any]}
      components={markdownComponents}
    >
      {children}
    </Markdown>
  );
};
